import React, { Component } from "react"
import trans from 'trans'
import { connect } from "react-redux"

import { ItemInterface, ReactSortable } from "react-sortablejs"

import {
    css,
    GridCell,
    Grid,
    Toggler,
    Theme,
    modal,
    Icon,
    Switcher,
    Tr,
} from "@bdfx/mergado-ui-kit"
import { Slider } from "@mui/material";

import { invertColor } from "app/helpers/other"
import { BRUSH_MIN_SPACING, RANDOM_ARRAY } from "shape-entities/components/brush-svg"
import TransparentColor from "./transparent-color"
import { URL } from "app/config/api-config"
import { isLight } from "app/helpers/colors"
import { AnyPoint, BrushFormType, FormNameType } from "app/types/model-types"
import { StyledSeparator, StyledSeparatorNoLine } from './common-shape-form';
import store from "store";
import { Provider } from "react-redux";
import { stringToHash } from "./helpers";
import BrushShape from "shape-entities/types/BrushShape";
import { selectFormValues, updateInputValue as change } from "app/reducers/form"
import Shape from "shape-entities/types/shape";

const BRUSH_PREVIEW_SIZE = 40

export const LINEWIDTH_PROFILE_UNIFORM = 'uniform'
export const LINEWIDTH_PROFILE_FADE_IN = 'fade_in'
export const LINEWIDTH_PROFILE_FADE_OUT = 'fade_out'
export const LINEWIDTH_PROFILE_FADING = 'fading'
export const LINEWIDTH_PROFILE_WAVE = 'wave'

const ALL_LINEWIDTH_PROFILES = [
    LINEWIDTH_PROFILE_UNIFORM,
    LINEWIDTH_PROFILE_FADE_IN,
    LINEWIDTH_PROFILE_FADE_OUT,
    LINEWIDTH_PROFILE_FADING,
    //LINEWIDTH_PROFILE_WAVE,   //TODO!!!
]

const ALL_PATTERNS = ['1.svg', '2.svg', '3.svg', '4.svg', '5.svg', '6.svg', '1.png', '3.png']
const { tan, atan2, PI, sin, cos, round, sqrt } = Math

export const getLineWidth = (normalizedPosition: number, absolutePosition: number, radius: number, lineWidthProfile: string, theta: number): number => {
    const effectLength = 5 * radius
    switch (lineWidthProfile) {
        case LINEWIDTH_PROFILE_FADE_OUT:
            return round(.5 * radius * cos(normalizedPosition * PI / 2))
        case LINEWIDTH_PROFILE_FADE_IN:
            //return absolutePosition <= effectLength ? round(.5*radius * sin(.5*(absolutePosition/effectLength) * PI/2)) : .5*radius
            return round(.5*radius * sin(normalizedPosition * PI/2))
        case LINEWIDTH_PROFILE_FADING:
            return round(.5 * radius * sin(normalizedPosition * PI))
        case LINEWIDTH_PROFILE_WAVE:
            return round(.5*radius * (sin(normalizedPosition * PI/4) + sin(theta)))
        default:
            return round(.5*radius)
    }
}

export const getPathFromPoints = (points: AnyPoint[], radius: number, lineWidthProfile: string): {
    base: string,
    around: string,
    bezier: string,
    controlPoints: any[],
    points: (AnyPoint & { angle: number })[]
} => {
    const countPoints = points.length

    let base = []
    let above = []
    let belowReverse = []
    let aboveWithControls = []
    let belowReverseWithControls = []
    let controlPoints = []
    let pp = []

    let absoluteLineLength = 0


    points.forEach((p, i) => {
        const isFirst = i === 0
        const isLast = i > countPoints - 1
        const isOnePoint = countPoints === 1

        let dx = 0, dy = 0
        if (!isOnePoint) {
            if (isFirst) {
                dx = points[1].x - points[0].x
                dy = points[1].y - points[0].y
            } else if (typeof points[i + 1] !== 'undefined') {
                dx = points[i + 1].x - points[i - 1].x
                dy = points[i + 1].y - points[i - 1].y
            } else {
                dx = points[i].x - points[i - 1].x
                dy = points[i].y - points[i - 1].y
            }
        }

        //const dx = points[i - 1] ? points[i].x - points[i - 1].x : 0
        //const dy = points[i - 1] ? points[i].y - points[i - 1].y : 0

        absoluteLineLength += sqrt(dx * dx + dy * dy)

        const theta = atan2(dy, dx)

        const s = sin(theta)
        const c = cos(theta)

        const normalizedPosition = countPoints > 1 ? i / (countPoints - 1) : 0    //something in range [0, ..., 1]
        const linewidth = getLineWidth(normalizedPosition, absoluteLineLength, radius, lineWidthProfile, theta)

        const aboveX = p.x - linewidth * s
        const aboveY = p.y + linewidth * c

        const belowX = p.x + linewidth * s
        const belowY = p.y - linewidth * c

        const dcx = (isLast || isFirst) ? 0 : linewidth * c
        const dcy = (isLast || isFirst) ? 0 : linewidth * s

        const aboveCx = aboveX - dcx
        const aboveCy = aboveY - dcy

        const belowCx = belowX + dcx
        const belowCy = belowY + dcy

        const aboveCx2 = 2 * aboveX - aboveCx
        const aboveCy2 = 2 * aboveY - aboveCy

        const belowCx2 = 2 * belowX - belowCx
        const belowCy2 = 2 * belowY - belowCy

        base.push(`${p.x},${p.y}`)
        above.push(`${aboveX},${aboveY}`)
        belowReverse.push(`${belowX},${belowY}`)

        aboveWithControls.push(`${aboveCx},${aboveCy} ${aboveX},${aboveY}`)
        belowReverseWithControls.push(`${belowCx},${belowCy} ${belowX},${belowY}`)

        controlPoints.push({
            x: aboveX,
            y: aboveY,
            cx: aboveCx,
            cy: aboveCy,
            cx2: aboveCx2,
            cy2: aboveCy2
        })

        controlPoints.push({         //??
            x: belowX,
            y: belowY,
            cx: belowCx,
            cy: belowCy,
            cx2: belowCx2,
            cy2: belowCy2,
        })

        pp.push({
            x: p.x,
            y: p.y,
            angle: theta * 180 / PI,
        })
    })


    return {
        base: `M ${base[0]} L${base.join(' ')}`,
        around: `M ${above[0]} L${above.join(' ')} ${[...belowReverse].reverse().join(' ')} Z`,
        bezier: `M ${aboveWithControls[0]} S${aboveWithControls.join(' ')} ${[...belowReverseWithControls].reverse().join(' ')} Z`,
        controlPoints,
        points: pp,
    }
}

export const getSVGProfile = (profile: string, color: string, radius: number, spacing: number): JSX.Element => {
    const stepSize = 2 * radius * spacing / 100

    const previewPoints: AnyPoint[] = [
        { x: radius, y: radius },
        { x: radius + stepSize, y: radius },
        { x: radius + 2 * stepSize, y: radius },
        { x: radius + 3 * stepSize, y: radius },
        { x: radius + 4 * stepSize, y: radius },
    ]

    const previewPointsEight: AnyPoint[] = [
        { x: radius, y: -radius },
        { x: radius + stepSize, y: 0 },
        { x: radius + 2 * stepSize, y: radius },
        { x: radius + 3 * stepSize, y: 0 },
        { x: radius + 4 * stepSize, y: -radius },
    ]
    const isVariableLineWidth = profile && profile !== LINEWIDTH_PROFILE_UNIFORM

    const paths = getPathFromPoints(profile === LINEWIDTH_PROFILE_WAVE ? previewPointsEight : previewPoints, radius, profile)

    return <svg viewBox={`0 0 ${3 * radius + 4 * stepSize} ${2 * radius}`} width={3 * radius + 4 * stepSize} height={2*radius} xmlns="http://www.w3.org/2000/svg" style={{display: 'block', height: '28px', width: '100%'}}>
        <g fill={color} stroke={invertColor(color)}>
            {isVariableLineWidth ?
                <path
                    stroke={color}
                    fill={color}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d={paths.around}
                    className='fill-path'
                /> :
                <path
                    fill={'none'}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    stroke={color}
                    strokeWidth={radius}
                    d={paths.base}
                />
            }
        </g>
    </svg>
}

interface Props {
    formName: FormNameType
    shape: BrushShape | Shape
    brushForm: BrushFormType
    change: typeof change
}

interface State {
}

class BrushForm extends Component<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
        }
    }

    changeBrushStroke = (i: number, field: string, value: any): void => {
        const { shape, formName } = this.props
        const brushStrokes = shape.brush_strokes && typeof shape.brush_strokes === 'string' ? JSON.parse(shape.brush_strokes) : shape.brush_strokes
        let updatedBrushStrokes = [...brushStrokes]
        let brushStroke = { ...brushStrokes[i] }
        brushStroke[field] = value
        updatedBrushStrokes[i] = brushStroke
        this.props.change(`${formName}.shapes.${shape.index}`, 'brush_strokes', JSON.stringify(updatedBrushStrokes))
    }

    deleteBrushStroke = (i: number) : void => {
        const { shape, formName } = this.props
        const brushStrokes = shape.brush_strokes && typeof shape.brush_strokes === 'string' ? JSON.parse(shape.brush_strokes) : shape.brush_strokes
        let updatedBrushStrokes = []
        brushStrokes.forEach((brushStroke, j) => {
            if (i !== j) {
                updatedBrushStrokes.push(brushStroke)
            }
        })
        this.props.change(`${formName}.shapes.${shape.index}`, 'brush_strokes', JSON.stringify(updatedBrushStrokes))
    }

    reorder = (list: ItemInterface[]) : void => {
        const { shape, formName } = this.props

        const reversedList = list.reverse()

        const brushStrokes = shape.brush_strokes && typeof shape.brush_strokes === 'string' ? JSON.parse(shape.brush_strokes) : shape.brush_strokes
        const brushStrokesReversed = Array.isArray(brushStrokes) ? brushStrokes.reverse() : null
        const c = brushStrokes.length - 1

        if (brushStrokesReversed) {
            const newOrder = reversedList.filter(i => typeof i !== 'undefined').map(i => i.id)
            const oldOrder = brushStrokes.map((brushStrokes: BrushShape, idx: number) => brushStrokes.id ?? c-idx)
            if (newOrder.join(',') !== oldOrder.join(',')) {
                const reordered = newOrder.map((idx: number) => brushStrokesReversed[idx])
                this.props.change(`${formName}.shapes.${shape.index}`, 'brush_strokes', JSON.stringify(reordered))
            }
        }
    }

    render() {
        const { brushForm, shape } = this.props

        const stepSize = 2 * brushForm.radius * brushForm.spacing / 100

        const previewPoints: AnyPoint[] = [
            { x: brushForm.radius, y: brushForm.radius },
            { x: brushForm.radius + stepSize, y: brushForm.radius },
            { x: brushForm.radius + 2 * stepSize, y: brushForm.radius },
            { x: brushForm.radius + 3 * stepSize, y: brushForm.radius },
            { x: brushForm.radius + 4 * stepSize, y: brushForm.radius },
        ]

        const isVariableLineWidth = brushForm.linewidth_profile && brushForm.linewidth_profile !== LINEWIDTH_PROFILE_UNIFORM
        const paths = getPathFromPoints(previewPoints, brushForm.radius, brushForm.linewidth_profile)

        const brushStrokes = shape.brush_strokes && typeof shape.brush_strokes === 'string' ? JSON.parse(shape.brush_strokes) : shape.brush_strokes
        const brushStrokesReversed = Array.isArray(brushStrokes) ? brushStrokes.reverse() : null
        const list = brushStrokesReversed ? brushStrokesReversed.map((brushStroke, idx) => { return { id: idx, selected: false } }) : []
        const c = brushStrokesReversed ? brushStrokesReversed.length - 1 : 0

        return (
            <React.Fragment>
                {brushStrokes && brushStrokes.length > 0 &&
                    <React.Fragment>
                        <GridCell style={{ alignSelf: 'center' }}>{trans('mode')}</GridCell>
                        <GridCell style={{
                            alignSelf: 'center',
                            padding: '10px',
                            ' > div': {
                                display: 'grid',
                            },
                            ' .ToggleGroup': {
                                width: '100%',
                                display: 'grid',
                                gridTemplateColumns: 'auto auto'
                            }
                        }}>
                            {/* <Toggler
                    name={`brushForm.is_eraser`}
                    title={trans('eraser')}
                    boolean={true}
                    big={false}
                    onLabel={<Icon type={'eraser-radix'} text={trans('eraser')} />}
                    offLabel={<Icon type={'paint-brush'} text={trans('brush')} />}
                /> */}
                            <Switcher
                                defaultValue={'off'}
                                value={brushForm.is_eraser ? 'on' : 'off'}
                                onChange={(value) => this.props.change('brushForm', 'is_eraser', value === 'on')}
                                items={[
                                    { label: trans('brush'), text: <Icon type={'paint-brush'} text={trans('brush')} />, value: "off" },
                                    { label: trans('eraser'), text: <Icon type={'eraser-radix'} text={trans('eraser')} />, value: "on" },
                                ]}
                            />
                        </GridCell>
                    </React.Fragment>
                }

                <GridCell style={{ alignSelf: 'center' }}>{trans('radius')} [px]</GridCell>
                <GridCell style={{ alignSelf: 'center', padding: '20px' }}>
                    {/* <Range name="brushForm.radius" /> */}
                    <Slider
                        value={brushForm.radius}
                        min={1}
                        max={100}
                        size={"small"}
                        valueLabelDisplay="on"
                        onChange={(event: Event, value: number) => this.props.change('brushForm', 'radius', value)}
                    />
                </GridCell>

                {!brushForm.is_eraser &&
                    <React.Fragment>
                        <GridCell style={{ alignSelf: 'center' }}>
                            <div>{trans('brush_spacing')} [%]</div>
                            {brushForm.spacing <= BRUSH_MIN_SPACING &&
                                <div style={{ color: Theme.red, textAlign: 'center', fontWeight: 'bold' }}>{trans('turned_off')}</div>
                            }
                        </GridCell>
                        <GridCell style={{ alignSelf: 'center', padding: '20px' }}>
                            {/* <Range name="brushForm.spacing" min={1} max={100} /> */}
                            <Slider
                                value={brushForm.spacing}
                                min={1}
                                max={100}
                                size={"small"}
                                valueLabelDisplay={brushForm.spacing > BRUSH_MIN_SPACING ? "on" : "off"}
                                marks={[
                                    { value: 5, label: trans('turned_off') },
                                    { value: 50, label: '50%' },
                                    { value: 100, label: '100%' }
                                ]}
                                onChange={(event: Event, value: number) => this.props.change('brushForm', 'spacing', value)}
                            />
                        </GridCell>

                        <GridCell style={{ alignSelf: 'center' }}>{trans('preview')}</GridCell>
                        <GridCell style={{ textAlign: 'center', alignSelf: 'center' }}>
                            {brushForm.spacing > BRUSH_MIN_SPACING &&
                                <svg viewBox={`0 0 ${4 * stepSize + 2 * brushForm.radius + 2} ${2 * brushForm.radius}`} xmlns="http://www.w3.org/2000/svg" style={{ height: brushForm.radius, width: 'auto' }}>
                                    <defs>
                                        <image
                                            id={'brush-pattern'}
                                            width={2 * brushForm.radius}
                                            height={2 * brushForm.radius}
                                            xlinkHref={brushForm.color ? `${URL.laravelBase}get-brush-pattern/${brushForm.pattern}/${brushForm.color.substring(1)}` : `${URL.laravelBase}img/brush-strokes/${brushForm.pattern}`}
                                        />
                                    </defs>

                                    {!brushForm.pattern && brushForm.color &&
                                        <g fill={brushForm.color} stroke={invertColor(brushForm.color)}>
                                            {isVariableLineWidth ?
                                                <path
                                                    stroke={brushForm.color}
                                                    fill={brushForm.color}
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                    strokeWidth={2}
                                                    d={paths.around}
                                                    className='fill-path'
                                                /> :
                                                <path
                                                    fill={'none'}
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                    stroke={brushForm.color}
                                                    strokeWidth={brushForm.radius}
                                                    d={paths.base}
                                                />
                                            }

                                            {previewPoints.map((p, i) => {
                                                const normalizedPosition = i / (previewPoints.length - 1)
                                                const linewidth = getLineWidth(normalizedPosition, i * brushForm.radius, brushForm.radius, brushForm.linewidth_profile, 0)
                                                return <circle key={`preview_circle_${i}`} r={linewidth} cx={p.x} cy={p.y} />
                                            })}
                                        </g>
                                    }

                                    {brushForm.pattern &&
                                        previewPoints.map((p, i) => {
                                            const newAngle = brushForm.pattern_rotate ? RANDOM_ARRAY[i] : 0
                                            const normalizedPosition = previewPoints.length > 1 ? i / (previewPoints.length - 1) : 0    //something in range [0, ..., 1]
                                            const linewidth = getLineWidth(normalizedPosition, i * brushForm.radius, brushForm.radius, brushForm.linewidth_profile, newAngle)
                                            const scale = 2 * linewidth / brushForm.radius
                                            const r = brushForm.radius * scale

                                            return <use
                                                key={`preview_pattern_${i}`}
                                                href={'#brush-pattern'}
                                                transform={`translate(${p.x - r}, ${p.y - r}), rotate(${newAngle}, ${r}, ${r}), scale(${scale})`}
                                            />
                                        })
                                    }
                                </svg>
                            }
                        </GridCell>

                        <GridCell style={{ alignSelf: 'center' }}>{trans('brush_color')}</GridCell>
                        <GridCell style={{ alignSelf: 'center' }}>
                            {!brushForm.is_eraser &&
                                <TransparentColor
                                    name={'brushForm.color'}
                                    value={brushForm.color}
                                    notRequired={!!brushForm.pattern}
                                    changeValue={(color: string) =>this.props.change('brushForm', 'color', color)}
                                />
                            }
                        </GridCell>

                        <GridCell style={{ alignSelf: 'center' }}>{trans('brush_pattern')}</GridCell>
                        <GridCell style={{ alignSelf: 'center' }}>
                            <StyledBrushStrokesGrid brushColor={brushForm.is_eraser ? '#ffffff' : brushForm.color}>
                                <GridCell
                                    className="clickable"
                                    onClick={() => this.props.change('brushForm', 'pattern', null)}
                                    style={{ border: `3px solid ${!brushForm.pattern ? (brushForm.is_eraser ? '#ccc' : brushForm.color) : 'transparent'}` }}>
                                    <div style={{
                                        width: `${BRUSH_PREVIEW_SIZE - 2}px`,
                                        height: `${BRUSH_PREVIEW_SIZE - 2}px`,
                                        borderRadius: '50%',
                                        margin: '4px 0px',
                                        background: brushForm.is_eraser ? 'white' : brushForm.color,
                                        opacity: brushForm.is_eraser ? .5 : 1,
                                    }}>&nbsp;</div>
                                </GridCell>
                                {ALL_PATTERNS.map(pattern => <GridCell
                                    key={`pattern_${pattern}`}
                                    className="clickable"
                                    onClick={() => this.props.change('brushForm', 'pattern', pattern)}
                                    style={{
                                        border: `3px solid ${pattern === brushForm.pattern ? (brushForm.is_eraser ? '#ccc' : brushForm.color) : 'transparent'}`,
                                        opacity: brushForm.is_eraser ? .5 : 1,

                                    }}>
                                    <img
                                        className="brush"
                                        src={brushForm.color ?
                                            `${URL.laravelBase}get-brush-pattern/${pattern}/${brushForm.is_eraser ? 'ffffff' : brushForm.color.substring(1)}` :
                                            `${URL.laravelBase}img/brush-strokes/${pattern}`
                                        }
                                    />
                                </GridCell>)}
                            </StyledBrushStrokesGrid>
                        </GridCell>

                        {brushForm.pattern &&
                            <React.Fragment>
                                <GridCell style={{ alignSelf: 'center' }}>{trans('brush_rotate_pattern')}</GridCell>
                                <GridCell style={{ alignSelf: 'center' }}>
                                    <Toggler name={`brushForm.pattern_rotate`} title={trans('brush_rotate_pattern')} boolean={true} big={false} />
                                </GridCell>
                            </React.Fragment>
                        }

                        <React.Fragment>
                            <GridCell style={{ alignSelf: 'center' }}>{trans('linewidth_profile')}</GridCell>
                            <StyledLineWidthProfiles>
                                {ALL_LINEWIDTH_PROFILES.map((profile) => {
                                    const isActive = profile === brushForm.linewidth_profile || (profile === 'uniform' && !brushForm.linewidth_profile)
                                    return <React.Fragment key={`profile_${profile}`}>
                                        <GridCell
                                            className={`clickable ${isActive ? 'active' : ''}`}
                                            title={trans(profile)}
                                            onClick={() => this.props.change('brushForm', 'linewidth_profile', profile === LINEWIDTH_PROFILE_UNIFORM ? null : profile)}>
                                            {getSVGProfile(profile, brushForm.color, brushForm.radius, 100)}
                                        </GridCell>
                                        <GridCell>{trans(profile)}</GridCell>
                                    </React.Fragment>
                                })}
                            </StyledLineWidthProfiles>
                        </React.Fragment>

                    </React.Fragment>
                }

                {brushStrokesReversed && Array.isArray(brushStrokesReversed) &&
                    <React.Fragment>
                        <GridCell col={'1/-1'}><StyledSeparatorNoLine /></GridCell>
                        <GridCell col={'1/-1'}>
                            <EditBrushStrokeTable>
                                <ReactSortable list={list} setList={this.reorder} handle=".drag-handle" tag={'tbody'}>
                                    {brushStrokesReversed.map((brushStroke, i) => <Tr rowId={`${i}`} key={`brush_stroke_${shape.index}_${stringToHash(JSON.stringify(brushStroke))}`}>
                                        <td id={`bs_${i}`} className="drag-handle">
                                            <Icon
                                                type="drag-handle-dots-2-radix"
                                                className="unhover"
                                                style={{ cursor: "grab", textAlign: "center" }}
                                                color="#888"
                                                size={18}
                                            />
                                        </td>
                                        <td className="clickable" onClick={() => this.changeBrushStroke(i, 'is_eraser', !brushStroke.is_eraser)}>
                                            <Icon type={brushStroke.is_eraser ? 'eraser-radix' : 'paint-brush'} />
                                        </td>
                                        <td className="clickable" onClick={() => modal({
                                            overlayClassName: 'brush-pattern-modal',
                                            title: trans('brush_pattern'),
                                            closeOnEscape: true,
                                            closeOnClickOutside: true,
                                            message: (close) => <Provider store={store}>
                                                <StyledBrushStrokesGrid brushColor={brushStroke.is_eraser ? '#cccccc' : brushStroke.color}>
                                                    <GridCell
                                                        className="clickable"
                                                        onClick={() => this.changeBrushStroke(i, 'pattern', null)}
                                                        style={{ border: `3px solid ${!brushStroke.pattern ? brushStroke.color : 'transparent'}` }}>
                                                        <div style={{
                                                            width: `${BRUSH_PREVIEW_SIZE - 2}px`,
                                                            height: `${BRUSH_PREVIEW_SIZE - 2}px`,
                                                            borderRadius: '50%',
                                                            margin: '4px 0px',
                                                            background: brushStroke.is_eraser ? '#cccccc' : brushStroke.color,
                                                        }}>&nbsp;</div>
                                                    </GridCell>
                                                    {ALL_PATTERNS.map(pattern => <GridCell
                                                        key={`pattern_${pattern}`}
                                                        className="clickable"
                                                        onClick={() => this.changeBrushStroke(i, 'pattern', pattern)}
                                                        style={{ border: `3px solid ${pattern === brushStroke.pattern ? brushStroke.color : 'transparent'}` }}>
                                                        <img
                                                            className="brush"
                                                            src={brushStroke.color ?
                                                                `${URL.laravelBase}get-brush-pattern/${pattern}/${brushStroke.is_eraser ? 'cccccc' : brushStroke.color.substring(1)}` :
                                                                `${URL.laravelBase}img/brush-strokes/${pattern}`
                                                            }
                                                        />
                                                    </GridCell>)}
                                                </StyledBrushStrokesGrid>
                                            </Provider>
                                        })}>
                                            {brushStroke.pattern ?
                                                <img
                                                    src={`${URL.laravelBase}get-brush-pattern/${brushStroke.pattern}/${brushStroke.color && !brushStroke.is_eraser ? brushStroke.color.substring(1) : 'cccccc'}`}
                                                    width={brushStroke.radius}
                                                    height={brushStroke.radius}
                                                    style={{ maxWidth: '40px', maxHeight: '40px', opacity: brushStroke.is_eraser ? .5 : 1 }}
                                                /> : <div
                                                    style={{
                                                        width: `${brushStroke.radius}px`,
                                                        height: `${brushStroke.radius}px`,
                                                        background: brushStroke.is_eraser ? 'transparent' : brushStroke.color,
                                                        borderRadius: '50%',
                                                        border: `1px solid ${brushStroke.is_eraser ? '#cccccc' : 'transparent'}`,
                                                        maxWidth: '40px',
                                                        maxHeight: '40px',
                                                    }}
                                                />
                                            }
                                        </td>
                                        <td>
                                            {!brushStroke.is_eraser &&
                                                <div style={{ width: "100px" }}>
                                                    <TransparentColor
                                                        value={brushStroke.color}
                                                        changeValue={(color: string) => this.changeBrushStroke(i, 'color', color)}
                                                    />
                                                </div>
                                            }
                                        </td>
                                        <td>
                                            <div style={{ width: "100px" }}>
                                                <Slider
                                                    value={brushStroke.radius}
                                                    min={1}
                                                    max={200}
                                                    size={"small"}
                                                    valueLabelDisplay="on"
                                                    onChange={(event: Event, value: number) => this.changeBrushStroke(i, 'radius', value)}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ width: "100px" }}>
                                                <Slider
                                                    value={brushStroke.spacing}
                                                    min={1}
                                                    max={100}
                                                    size={"small"}
                                                    valueLabelDisplay="on"
                                                    onChange={(event: Event, value: number) => this.changeBrushStroke(i, 'spacing', value)}
                                                />
                                            </div>
                                        </td>
                                        <td className="clickable" onClick={() => this.deleteBrushStroke(i)}>
                                            <Icon type={'close'} color={Theme.red} />
                                        </td>
                                    </Tr>
                                    )}
                                </ReactSortable>
                            </EditBrushStrokeTable>
                        </GridCell>
                        <GridCell col={'1/-1'}><StyledSeparatorNoLine /></GridCell>
                    </React.Fragment>
                }
            </React.Fragment>
        )
    }
}

const EditBrushStrokeTable = css('table')({
    width: '100%',
    ' td': {
        textAlign: 'center',
    }
})

const StyledLineWidthProfiles = css(GridCell)({
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    alignSelf: 'center',
    ' .muk-gridcell': {
        alignSelf: 'center',
    },
    ' > div': {
        border: '3px solid transparent',
    },
    ' > div.active': {
        border: `3px solid ${Theme.steelblue}`,
    }
})

const StyledBrushStrokesGrid = css(Grid)({
    gridTemplateColumns: `repeat(auto-fill, ${BRUSH_PREVIEW_SIZE + 4}px)`,
    gridGap: '3px',
    ' img.brush': {
        width: `${BRUSH_PREVIEW_SIZE}px`,
        height: `${BRUSH_PREVIEW_SIZE}px`,
    },
    ' .muk-gridcell': {
        textAlign: 'center',
        alignSelf: 'center',
    }
}, props => {
    const isLightColor = isLight(props.brushColor)
    const color = isLightColor ? '#3a3a3a' : '#ccc'
    return {
        backgroundColor: isLightColor ? '#000' : undefined,
        backgroundImage: `linear-gradient(45deg, ${color} 25%, transparent 25%),linear-gradient(135deg, ${color} 25%, transparent 25%),linear-gradient(45deg, transparent 75%, ${color} 75%),linear-gradient(135deg, transparent 75%, ${color} 75%)`,
        backgroundSize: '25px 25px',
        backgroundPosition: '0 0, 12.5px 0, 12.5px -12.5px, 0px 12.5px',
    }
})


function mapStateToProps(state) {
    return {
        brushForm: selectFormValues(state, 'brushForm'),
    }
}

export default connect(mapStateToProps, {
    change,
})(BrushForm)